import { useState } from 'react';
import type { ReactNode } from 'react';
import { useIsTabletUp } from '@/shared/hooks/useMediaQuery';
import { env } from '@/shared/lib/env';
import { SidebarProvider, useSidebar } from '@/shared/ui/layout/SidebarContext';
import { HeaderActionsProvider, PageHeaderSlotProvider } from '@/shared/ui/layout/PageHeaderSlot';
import { BottomNav } from '@/shared/ui/navigation/BottomNav';
import { SideRail } from '@/shared/ui/navigation/SideRail';
import { cn } from '@/shared/utils/cn';

type AppShellProps = {
  children: ReactNode;
  hideNavigation?: boolean;
  headerActions?: ReactNode;
};

const AppShellInner = ({ children, hideNavigation = false, headerActions }: AppShellProps) => {
  const isTabletUp = useIsTabletUp();
  const { collapsed } = useSidebar();
  // State (not a plain ref) so the portal target re-renders PageHeader once mounted.
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  const showSide = isTabletUp && !hideNavigation;
  const showBottom = !isTabletUp && !hideNavigation;

  return (
    <PageHeaderSlotProvider value={showSide ? headerSlot : null}>
      <HeaderActionsProvider value={hideNavigation ? null : headerActions}>
        <div className={cn('flex min-h-screen w-full bg-surface-soft text-ink')}>
          {showSide && (
            <header className="fixed inset-x-0 top-0 z-30 flex h-16 items-stretch border-b border-slate-200 bg-white">
              <div className="flex w-60 shrink-0 items-center gap-2 border-r border-slate-200 px-4 lg:w-64 lg:px-6">
                <img
                  src="/logo.jpg"
                  alt="Tez Cargo"
                  className="h-10 w-10 shrink-0 rounded-lg object-contain shadow-sm"
                />
                <span className="min-w-0 flex-1 truncate text-base font-semibold text-ink">
                  {env.appName}
                </span>
              </div>
              <div ref={setHeaderSlot} className="min-w-0 flex-1" />
            </header>
          )}
          {showSide && <SideRail />}
          <main
            className={cn(
              'flex min-h-screen w-full flex-1 flex-col transition-[margin]',
              showSide && 'pt-16',
              showSide && (collapsed ? 'md:ml-16' : 'md:ml-60 lg:ml-64'),
              showBottom && 'pb-20',
            )}
          >
            <div className="mx-auto w-full max-w-3xl flex-1 lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl">
              {children}
            </div>
          </main>
          {showBottom && <BottomNav />}
        </div>
      </HeaderActionsProvider>
    </PageHeaderSlotProvider>
  );
};

export const AppShell = (props: AppShellProps) => (
  <SidebarProvider>
    <AppShellInner {...props} />
  </SidebarProvider>
);
